import React from 'react';
import { string, bool, func } from 'prop-types';
import { propTypes } from '../../util/types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm, Field } from 'react-final-form';
import {
  Form,
  PrimaryButton,
  FieldTextInput,
  FieldSelect,
  ImageFromFile,
  Icon,
} from '../../components';
import * as validators from '../../util/validators';
import classNames from 'classnames';
import { getMediaUrl } from '../../util/uploadedMedia';

import css from './NewOrganizationForm.module.css';

const ACCEPT_IMAGES = 'image/*';
const BIO_MIN_LENGTH = 200;
const BIO_MAX_LENGTH = 700;

const yearsOptions = Array.from({ length: new Date().getFullYear() - 1979 }, (_, i) => ({
  key: (new Date().getFullYear() - i).toString(),
  label: (new Date().getFullYear() - i).toString(),
}));

const NewOrganizationFormComponent = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        className,
        rootClassName,
        handleSubmit,
        intl,
        form,
        invalid,
        pristine,
        values,
        createOrganizationInProgress,
        createOrganizationError,
        uploadImageInProgress,
      } = fieldRenderProps;

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = createOrganizationInProgress || uploadImageInProgress;
      const submitDisabled = invalid || pristine || submitInProgress;

      // name
      const nameLabel = intl.formatMessage({
        id: 'OrganizationSettingsForm.nameLabel',
      });
      const namePlaceholder = intl.formatMessage({
        id: 'OrganizationSettingsForm.namePlaceholder',
      });
      const nameRequiredMessage = intl.formatMessage({
        id: 'OrganizationSettingsForm.nameRequired',
      });
      const nameRequired = validators.required(nameRequiredMessage);

      // website
      const websiteLabel = intl.formatMessage({
        id: 'OrganizationSettingsForm.websiteLabel',
      });
      const websitePlaceholder = intl.formatMessage({
        id: 'OrganizationSettingsForm.websitePlaceholder',
      });
      const websiteValidMessage = intl.formatMessage({
        id: 'OrganizationSettingsPage.websiteValid',
      });
      const websiteValid = validators.validURL(websiteValidMessage);

      // bio
      const bioLabel = intl.formatMessage({
        id: 'OrganizationSettingsForm.bioLabel',
      });
      const bioPlaceholder = intl.formatMessage({
        id: 'OrganizationSettingsForm.bioPlaceholder',
      });
      const bioRequiredMessage = intl.formatMessage({
        id: 'OrganizationSettingsForm.bioRequired',
      });
      const bioMinLengthMessage = intl.formatMessage(
        {
          id: 'OrganizationSettingsForm.bioMinLength',
        },
        { maxLength: BIO_MIN_LENGTH }
      );
      const bioMaxLengthMessage = intl.formatMessage(
        {
          id: 'OrganizationSettingsForm.bioMaxLength',
        },
        { maxLength: BIO_MAX_LENGTH }
      );
      const bioRequired = validators.required(bioRequiredMessage);
      const bioMinLength = validators.minLength(bioMinLengthMessage, BIO_MIN_LENGTH);
      const bioMaxLength = validators.maxLength(bioMaxLengthMessage, BIO_MAX_LENGTH);

      // founded
      const foundedLabel = intl.formatMessage({
        id: 'OrganizationSettingsForm.foundedLabel',
      });
      const foundedPlaceholder = intl.formatMessage({
        id: 'OrganizationSettingsForm.foundedPlaceholder',
      });

      // founder
      const founderLabel = intl.formatMessage({
        id: 'OrganizationSettingsForm.founderLabel',
      });
      const founderPlaceholder = intl.formatMessage({
        id: 'OrganizationSettingsForm.founderPlaceholder',
      });

      // instagram
      const instagramLinkMessage = intl.formatMessage({
        id: 'OrganizationSettingsForm.instagramLink',
      });
      const instagramLinkPlaceholderMessage = intl.formatMessage({
        id: 'OrganizationSettingsForm.instagramLinkPlaceholder',
      });
      const instagramLinkValidMessage = intl.formatMessage({
        id: 'OrganizationSettingsForm.instagramLinkValidMessage',
      });

      const profileImage = values?.profileImage;

      const imageFromFile =
        profileImage && profileImage.lastModified ? (
          <ImageFromFile
            id={profileImage.name}
            rootClassName={css.uploadedImage}
            aspectRatioClassName={css.squareAspectRatio}
            file={profileImage}
          />
        ) : null;

      const avatarComponent =
        profileImage && profileImage?.id ? (
          <div className={css.uploadedImage}>
            <div className={css.squareAspectRatio}>
              <img
                className={css.rootForImage}
                src={getMediaUrl(profileImage, 'card')}
                alt={profileImage.name}
              />
            </div>
          </div>
        ) : null;

      const chooseAvatarLabel =
        imageFromFile || avatarComponent ? (
          <div className={css.avatarContainer}>
            {imageFromFile}
            {avatarComponent}
            <div className={css.changeAvatar}>
              <Icon name="edit" size={16} className={css.changeAvatarIcon} />
              <FormattedMessage id="ProfileImageForm.changeAvatar" />
            </div>
          </div>
        ) : (
          <div className={css.avatarPlaceholder}>
            <div className={css.avatarPlaceholderText}>
              <FormattedMessage id="ProfileImageForm.addYourProfilePicture" />
            </div>
            <div className={css.avatarPlaceholderTextMobile}>
              <FormattedMessage id="ProfileImageForm.addYourProfilePictureMobile" />
            </div>
          </div>
        );

      const errorMessage = createOrganizationError ? (
        <p className={css.error}>
          <FormattedMessage id="NewOrganizationForm.createError" />
        </p>
      ) : null;

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <div className={css.card}>
            <div className={css.cardHeader}>
              <h2 className={css.cardTitle}>
                <FormattedMessage id="NewOrganizationForm.cardTitle" />
              </h2>
              <p className={css.cardSubtitle}>
                <FormattedMessage id="NewOrganizationForm.cardSubtitle" />
              </p>
            </div>
            <div className={css.cardDivider} />
            <div className={css.cardContent}>
              <div className={css.imageUploadWrapper}>
                <Field
                  accept={ACCEPT_IMAGES}
                  id="profileImage"
                  name="profileImage"
                  label={chooseAvatarLabel}
                  type="file"
                  form={null}
                  disabled={false}
                >
                  {fieldProps => {
                    const { accept, id, input, label, disabled } = fieldProps;
                    const { name, type } = input;
                    const onChange = async e => {
                      const file = e.target.files[0];

                      if (file) {
                        form.change('profileImage', file);
                        form.blur('profileImage');
                      }
                    };

                    return (
                      <div>
                        <label htmlFor={id}>{label}</label>
                        <input
                          accept={accept}
                          id={id}
                          name={name}
                          className={css.editOrganizationProfileImageInput}
                          disabled={disabled}
                          onChange={onChange}
                          type={type}
                        />
                      </div>
                    );
                  }}
                </Field>
              </div>

              <FieldTextInput
                className={css.field}
                type="text"
                id="name"
                name="name"
                label={nameLabel}
                placeholder={namePlaceholder}
                validate={nameRequired}
              />
              <FieldTextInput
                className={css.field}
                type="text"
                id="website"
                name="website"
                label={websiteLabel}
                placeholder={websitePlaceholder}
                validate={websiteValid}
              />
              <FieldSelect
                className={css.field}
                id="founded"
                name="founded"
                options={yearsOptions}
                label={foundedLabel}
                placeholder={foundedPlaceholder}
              />
              <FieldTextInput
                className={css.field}
                type="text"
                id="founder"
                name="founder"
                label={founderLabel}
                placeholder={founderPlaceholder}
              />
              <FieldTextInput
                className={css.field}
                type="text"
                id="instagramLink"
                name="instagramLink"
                label={instagramLinkMessage}
                placeholder={instagramLinkPlaceholderMessage}
                validate={validators.validInstagramLink(instagramLinkValidMessage)}
              />
              <FieldTextInput
                className={css.field}
                type="textarea"
                id="bio"
                name="bio"
                label={bioLabel}
                placeholder={bioPlaceholder}
                validate={validators.composeValidators(bioRequired, bioMinLength, bioMaxLength)}
              />

              <PrimaryButton
                className={css.submitButton}
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
              >
                <FormattedMessage id="NewOrganizationForm.createOrganization" />
              </PrimaryButton>
            </div>
          </div>

          {errorMessage}
        </Form>
      );
    }}
  />
);

NewOrganizationFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  createOrganizationInProgress: false,
  createOrganizationError: null,
  uploadImageInProgress: false,
};

NewOrganizationFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  createOrganizationInProgress: bool.isRequired,
  createOrganizationError: propTypes.error,
  uploadImageInProgress: bool,

  // from injectIntl
  intl: intlShape.isRequired,
};

const NewOrganizationForm = compose(injectIntl)(NewOrganizationFormComponent);

NewOrganizationForm.displayName = 'NewOrganizationForm';

export default NewOrganizationForm;
